<template>
  <div class="fastfood-view">
    <!-- 左侧：分类栏 + 商品网格（图片/名称/价格，估清置灰） -->
    <div class="ff-main">
      <div class="ff-cats">
        <div class="ff-cat" :class="{ active: activeCategory === 0 }" @click="switchCategory(0)">全部</div>
        <div class="ff-cat" v-for="c in flatCategories" :key="c.id"
             :class="{ active: activeCategory === c.id }" @click="switchCategory(c.id)">{{ c.name }}</div>
      </div>
      <div class="ff-goods">
        <div class="ff-goods-head">
          <a-input-search v-model="searchKey" placeholder="搜索菜品名称" allowClear @search="loadMenu" />
        </div>
        <a-icon v-if="menuLoading" type="loading" class="ff-loading" />
        <div class="ff-grid" v-else>
          <div class="ff-card" v-for="g in goods" :key="g.id"
               :class="{ soldout: isSoldOut(g) }" @click="onGoodsClick(g)">
            <div class="ff-card-img">
              <img v-if="g.imgName" :src="imgUrl(g.imgName)" alt="" />
              <span v-else class="ff-img-holder">{{ firstChar(g) }}</span>
              <span v-if="isSoldOut(g)" class="ff-soldout">估清</span>
            </div>
            <div class="ff-card-name" :title="g.materialName">{{ g.materialName }}</div>
            <div class="ff-card-price">¥{{ fmt(g.minPrice) }}</div>
          </div>
        </div>
        <div v-if="!menuLoading && goods.length === 0" class="ff-goods-empty">该分类暂无在售菜品</div>
      </div>
    </div>
    <!-- 右侧：挂单列表 + 购物车面板 + 底部结算区 -->
    <div class="ff-side">
      <div class="ff-parked">
        <div class="ff-parked-head" @click="parkedExpanded = !parkedExpanded">
          <span>挂单（{{ parked.length }}）</span>
          <a-icon :type="parkedExpanded ? 'up' : 'down'" />
        </div>
        <div class="ff-parked-list" v-show="parkedExpanded">
          <div class="ff-parked-card" v-for="p in parked" :key="p.id" @click="resumeParked(p)">
            <div class="fp-row">
              <span class="fp-no">{{ p.order_no }}</span>
              <span class="fp-amount">¥{{ fmt(p.total_amount) }}</span>
            </div>
            <div class="fp-time">{{ p.create_time }} · 点击取单恢复</div>
          </div>
          <div v-if="parked.length === 0" class="fp-empty">暂无挂单</div>
        </div>
      </div>
      <div class="ff-cart">
        <div class="ff-cart-title">
          <span>当前账单</span>
          <a v-if="cart.length" class="ff-clear" @click="cart = []">清空待下单</a>
        </div>
        <div class="ff-cart-scroll">
          <template v-if="orderItems.length">
            <div class="ff-group-label">已下单（金额为后端计价）</div>
            <div class="ff-line saved" v-for="it in orderItems" :key="it.id"
                 :class="{ 'ff-line-returned': isReturned(it) }">
              <div class="fl-main">
                <div class="fl-name">{{ it.material_name }}
                  <a-tag v-if="isReturned(it)" color="red" class="fl-returned-tag"
                         :title="it.return_reason || ''">已退</a-tag>
                </div>
                <div class="fl-sub" v-if="optionsText(it.options_json)">{{ optionsText(it.options_json) }}</div>
                <div class="fl-sub" v-if="it.remark">备注：{{ it.remark }}</div>
              </div>
              <div class="fl-right">
                <div class="fl-qty">×{{ it.quantity }}</div>
                <div class="fl-amount">¥{{ fmt(it.all_price) }}</div>
                <a-button v-if="!isReturned(it)" size="small" type="danger" ghost icon="rollback"
                          class="fl-return-btn" @click="openReturnModal(it)">退菜</a-button>
              </div>
            </div>
          </template>
          <template v-if="cart.length">
            <div class="ff-group-label">待下单（价格为参考价）</div>
            <div class="ff-line" v-for="(line, idx) in cart" :key="line.key">
              <div class="fl-main">
                <div class="fl-name">{{ line.materialName }}</div>
                <div class="fl-sub" v-if="line.optionText">{{ line.optionText }}</div>
                <div class="fl-sub" v-if="line.remark">备注：{{ line.remark }}</div>
                <div class="qty-step">
                  <a-button size="small" @click="decQty(idx)">-</a-button>
                  <span class="qty-num">{{ line.quantity }}</span>
                  <a-button size="small" @click="line.quantity++">+</a-button>
                  <a-button size="small" type="danger" icon="close" class="fl-del" @click="cart.splice(idx, 1)" />
                </div>
              </div>
              <div class="fl-right">
                <div class="fl-amount">¥{{ fmt(line.unitRef * line.quantity) }}</div>
              </div>
            </div>
          </template>
          <div v-if="!orderItems.length && !cart.length" class="ff-cart-empty">点击左侧菜品开始开单</div>
        </div>
        <div class="ff-footer">
          <div class="ff-out-row">
            <a-radio-group v-model="isOut" :disabled="!!orderId">
              <a-radio value="1">店内就餐</a-radio>
              <a-radio value="2">打包带走</a-radio>
            </a-radio-group>
            <span class="ff-out-tip" v-if="orderId">已下单后消费方式不可更改</span>
          </div>
          <div class="ff-fee-row"><span>商品合计</span><span>¥{{ fmt(itemsTotal) }}</span></div>
          <div class="ff-fee-row" v-if="isOut === '2'">
            <span>打包费{{ boxTypeHint }}</span><span>¥{{ fmt(boxFee) }}</span>
          </div>
          <div class="ff-fee-row ff-total"><span>合计</span><span class="ff-total-num">¥{{ fmt(orderTotal) }}</span></div>
          <div class="ff-actions">
            <a-button :disabled="!orderId || acting" @click="handleVoid">作废</a-button>
            <a-button :disabled="!canPark || acting" :loading="acting && parking" @click="handlePark">挂单</a-button>
            <a-button type="primary" class="ff-settle-btn" :disabled="!canSettle || acting"
                      :loading="acting && !parking" @click="handleSettle">结算</a-button>
          </div>
        </div>
      </div>
    </div>
    <!-- 菜品选项弹窗（一期组件复用：规格/做法单选、加料多选加价、必选校验） -->
    <GoodsOptionsModal :visible="modalVisible" :goods="modalGoods"
                       @confirm="onModalConfirm" @cancel="modalVisible = false" />
    <!-- 结算弹窗（一期 Settle 能力重组：会员搜索/抹零/现金/微信扫码枪/支付宝扫码枪） -->
    <a-modal title="结算" :visible="settleVisible" :maskClosable="false" width="480px"
              :footer="null" @cancel="settleVisible = false">
      <div class="st-body">
        <div class="st-amounts" v-if="order">
          <div class="st-row"><span>商品合计</span><span>¥{{ fmt(order.total_amount) }}</span></div>
          <div class="st-row" v-if="Number(order.service_money) > 0"><span>服务费</span><span>¥{{ fmt(order.service_money) }}</span></div>
          <div class="st-row" v-if="Number(order.tableware_money) > 0"><span>打包费</span><span>¥{{ fmt(order.tableware_money) }}</span></div>
          <div class="st-row"><span>应收</span><span class="st-payable">¥{{ fmt(payablePreview) }}</span></div>
        </div>
        <div class="st-sec-title">会员</div>
        <div v-if="member" class="st-member">
          <span class="st-member-name">{{ member.contacts || '会员' }}</span>
          <span class="st-member-phone">{{ member.telephone || '' }}</span>
          <a-button size="small" @click="clearMember">清除</a-button>
        </div>
        <template v-else>
          <div class="st-member-search">
            <a-input v-model="memberKeyword" placeholder="手机号 / 姓名 / 卡号 / 会员码" @pressEnter="searchMember" />
            <a-button :loading="memberSearching" @click="searchMember">查询</a-button>
          </div>
          <div class="st-member-results" v-if="memberResults.length">
            <div class="st-member-item" v-for="m in memberResults" :key="m.memberId" @click="pickMember(m)">
              <span class="smi-name">{{ m.contacts || m.cardNo }}</span>
              <span class="smi-phone">{{ m.telephone || '' }}</span>
              <a-tag v-if="m.levelName" color="blue">{{ m.levelName }}</a-tag>
            </div>
          </div>
          <div class="st-tip">会员价优惠由后端结算时计算</div>
        </template>
        <div class="st-sec-title">抹零</div>
        <a-input-number v-model="eraseAmount" :min="0" :max="eraseMax" :precision="2" style="width: 160px" />
        <div class="st-sec-title">支付方式</div>
        <div class="st-pay-types">
          <div class="st-pay-type" :class="{ active: payType === 'cash' }" @click="switchPayType('cash')">现金</div>
          <div class="st-pay-type" :class="{ active: payType === 'wx' }" @click="switchPayType('wx')">微信扫码枪</div>
          <div class="st-pay-type" :class="{ active: payType === 'ali' }" @click="switchPayType('ali')">支付宝扫码枪</div>
        </div>
        <template v-if="payType !== 'cash'">
          <a-input v-model="authCode" placeholder="扫码枪录入顾客付款码，扫描后自动结算"
                   :disabled="settling" @pressEnter="confirmSettle" />
        </template>
        <a-button type="primary" size="large" block class="st-confirm"
                  :loading="settling" @click="confirmSettle">确认结算</a-button>
      </div>
    </a-modal>
    <!-- 结算成功：取餐号大字弹窗（callNo 居中大字号 + 语音播报触发） -->
    <a-modal :visible="callVisible" :footer="null" :maskClosable="false" centered @cancel="callVisible = false">
      <div class="callno-wrap" v-if="settleResult">
        <div class="callno-label">结算成功，取餐号</div>
        <div class="callno-num">{{ settleResult.callNo || '—' }}</div>
        <div class="callno-sub">
          实收 ¥{{ fmt(settleResult.payAmount) }}（{{ payText(settleResult.payType) }}）
        </div>
        <a-button type="primary" size="large" block @click="callVisible = false">完成</a-button>
      </div>
    </a-modal>
    <!-- 退菜弹窗（Task 8：理由必填，确认后触发 TCD 退菜单打印） -->
    <return-item-modal :visible="returnVisible"
                       :item-name="returnTarget ? returnTarget.material_name : ''"
                       :loading="returnLoading"
                       @cancel="returnVisible = false" @confirm="confirmReturn" />
    <!-- 结账单打印（Task 5）：结算成功自动调起，autoPrint 开启静默出票否则弹预览 -->
    <print-modal ref="printModal" />
  </div>
</template>

<script>
import { getFileAccessHttpUrl, getAction } from '@/api/manage'
import { menuList, orderCreate, orderAddItems, orderPark, orderResume, orderCancel,
  parkedList, orderDetail, orderSettle, orderItemReturn } from '@/api/plugin/dining'
import { cashierMemberSearch } from '@/api/plugin/member'
import GoodsOptionsModal from './GoodsOptionsModal'
import PrintModal from './PrintModal'
import ReturnItemModal from './components/ReturnItemModal.vue'

const POLL_INTERVAL = 15000 // 挂单列表轮询（页面隐藏暂停，照一期模式）

/**
 * 收银视图（/cashier/dining/fastfood，Task 7.1/7.2 克隆云贝收银界面）：
 * 左分类+商品网格（估清置灰）→ 选项弹窗（一期组件）→ 右侧本地开单车（数量增删/删除行）
 * + 挂单列表（一期 park/resume）+ 店内就餐/打包带走（isOut）+ 打包费（fast 设置
 * boxType 1=按单/2=按件×boxMoney，下单后端计算入单）+ 结算弹窗（会员搜索/抹零/
 * 现金/微信/支付宝扫码枪，一期 Settle 资产重组）→ 取餐号大字弹窗 + 语音播报。
 * 金额链以后端为准：下单前展示参考价，下单后 total/tableware 均为后端返回。
 */
export default {
  name: 'CashierFastfoodView',
  components: { GoodsOptionsModal, PrintModal, ReturnItemModal },
  props: {
    storeId: { type: Number, required: true },
    storeName: { type: String, default: '' }
  },
  data() {
    return {
      categories: [],
      goods: [],
      activeCategory: 0,
      searchKey: '',
      menuLoading: false,
      cart: [],
      modalVisible: false,
      modalGoods: null,
      orderId: null,
      order: null,
      isOut: '1',
      fastSet: {},
      parked: [],
      parkedExpanded: false,
      parkedTimer: null,
      acting: false,
      parking: false,
      settleVisible: false,
      settling: false,
      member: null,
      memberKeyword: '',
      memberResults: [],
      memberSearching: false,
      eraseAmount: 0,
      payType: 'cash',
      authCode: '',
      authTimer: null,
      settleResult: null,
      callVisible: false,
      // 退菜弹窗（Task 8：理由必填，触发 TCD）
      returnVisible: false,
      returnLoading: false,
      returnTarget: null
    }
  },
  computed: {
    flatCategories() {
      const list = []
      const walk = (nodes, level) => {
        (nodes || []).forEach(n => {
          list.push({ id: n.id, name: n.name, level: level })
          if (n.children && n.children.length) walk(n.children, level + 1)
        })
      }
      walk(this.categories, 0)
      return list
    },
    orderItems() {
      return (this.order && this.order.items) || []
    },
    // 已退行不计金额（Task 8：后端 total_amount 同口径）
    savedTotal() {
      return this.orderItems.reduce((sum, it) =>
        sum + (String(it.return_status) === '1' ? 0 : Number(it.all_price || 0)), 0)
    },
    cartTotal() {
      return this.cart.reduce((sum, line) => sum + line.unitRef * line.quantity, 0)
    },
    itemsTotal() {
      return this.savedTotal + this.cartTotal
    },
    totalQty() {
      let qty = 0
      // 已退行不计件（打包费按件预估口径，Task 8）
      this.orderItems.forEach(it => {
        if (String(it.return_status) !== '1') qty += Number(it.quantity || 0)
      })
      this.cart.forEach(line => { qty += Number(line.quantity || 0) })
      return qty
    },
    // 打包费：已下单取后端 tableware_money；未下单按 fast 设置预估（isOut='2' 打包带走才收）
    boxFee() {
      if (this.isOut !== '2') return 0
      if (this.orderId && this.order) return Number(this.order.tableware_money || 0)
      const boxMoney = Number(this.fastSet.boxMoney || 0)
      return String(this.fastSet.boxType) === '1' ? boxMoney : this.totalQty * boxMoney
    },
    boxTypeHint() {
      if (String(this.fastSet.boxType) === '1') return '（按单收取）'
      return '（' + this.totalQty + '件 × ¥' + this.fmt(this.fastSet.boxMoney || 0) + '）'
    },
    orderTotal() {
      return this.itemsTotal + this.boxFee
    },
    canPark() {
      return this.orderItems.length > 0 || this.cart.length > 0
    },
    canSettle() {
      return this.orderItems.length > 0 || this.cart.length > 0
    },
    eraseMax() {
      if (!this.order) return 0
      return Number(this.order.total_amount || 0) + Number(this.order.service_money || 0) +
        Number(this.order.tableware_money || 0)
    },
    payablePreview() {
      if (!this.order) return 0
      return this.eraseMax - (Number(this.eraseAmount) || 0)
    }
  },
  watch: {
    // 扫码枪无回车后缀时按码长自动结算（微信 1 开头 16~19 位；支付宝 25~30 开头）
    authCode(val) {
      const code = (val || '').trim()
      clearTimeout(this.authTimer)
      if (/^1\d{15,18}$/.test(code) || /^(2[5-9]|30)\d{16,28}$/.test(code)) {
        this.authTimer = setTimeout(() => {
          if (code === (this.authCode || '').trim()) {
            this.confirmSettle()
          }
        }, 300)
      }
    }
  },
  created() {
    this.loadMenu()
    this.loadFastSet()
    this.loadParked()
    this.startParkedPolling()
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  },
  beforeDestroy() {
    this.stopParkedPolling()
    clearTimeout(this.authTimer)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  },
  methods: {
    fmt(v) {
      return Number(v || 0).toFixed(2)
    },
    imgUrl(imgName) {
      return getFileAccessHttpUrl('systemConfig/static/' + String(imgName).split(',')[0])
    },
    firstChar(g) {
      return g.materialName ? g.materialName[0] : '菜'
    },
    isSoldOut(g) {
      return String(g.soldOut) === '1'
    },
    optionsText(json) {
      if (!json) return ''
      try {
        const arr = JSON.parse(String(json))
        if (!Array.isArray(arr)) return ''
        return arr.map(o => o.valueName).filter(Boolean).join('/')
      } catch (e) {
        return ''
      }
    },
    payText(t) {
      if (!t) return '-'
      return { cash: '现金', wx: '微信', ali: '支付宝' }[t] || t
    },
    // ============ 菜单（一期收银台商品接口） ============
    loadMenu() {
      this.menuLoading = true
      const params = { storeId: this.storeId, currentPage: 1, pageSize: 200 }
      if (this.activeCategory > 0) params.categoryId = this.activeCategory
      const kw = (this.searchKey || '').trim()
      if (kw) params.search = kw
      menuList(params).then(res => {
        if (res.code === 200 && res.data) {
          if (res.data.categories) this.categories = res.data.categories
          this.goods = res.data.rows || []
        } else {
          this.$message.error(res.message || '菜单查询失败')
        }
      }).catch(() => {}).finally(() => {
        this.menuLoading = false
      })
    },
    switchCategory(id) {
      if (this.activeCategory === id) return
      this.activeCategory = id
      this.loadMenu()
    },
    onGoodsClick(g) {
      if (this.isSoldOut(g)) {
        this.$message.warning('菜品已估清')
        return
      }
      if ((g.options || []).length > 0) {
        this.modalGoods = g
        this.modalVisible = true
      } else {
        this.addToCart({
          materialId: g.materialId,
          materialName: g.materialName,
          quantity: 1,
          remark: '',
          optionValueIds: [],
          optionText: '',
          unitRef: Number(g.minPrice || 0)
        })
      }
    },
    onModalConfirm(payload) {
      this.modalVisible = false
      this.addToCart({
        materialId: this.modalGoods.materialId,
        materialName: this.modalGoods.materialName,
        quantity: payload.quantity,
        remark: payload.remark,
        optionValueIds: payload.optionValueIds,
        optionText: payload.optionText,
        unitRef: Number(this.modalGoods.minPrice || 0) + payload.extraAmount
      })
    },
    // 同商品+同选项+同备注的行合并数量（一期购物车逻辑）
    addToCart(line) {
      const key = line.materialId + '|' + [...line.optionValueIds].sort((a, b) => a - b).join(',') + '|' + (line.remark || '')
      const exist = this.cart.find(i => i.key === key)
      if (exist) {
        exist.quantity += line.quantity
      } else {
        this.cart.push(Object.assign({ key: key }, line))
      }
    },
    decQty(idx) {
      const line = this.cart[idx]
      if (line.quantity > 1) {
        line.quantity--
      } else {
        this.cart.splice(idx, 1)
      }
    },
    // fast 设置：boxType/boxMoney（打包费）
    loadFastSet() {
      getAction('/api/plugin/dining/store-setting/fast', { storeId: this.storeId }).then(res => {
        if (res.code === 200 && res.data) this.fastSet = res.data
      }).catch(() => {})
    },
    // ============ 挂单列表（一期 park 接口） ============
    loadParked() {
      parkedList({ storeId: this.storeId }).then(res => {
        if (res.code === 200 && res.data) {
          this.parked = res.data.rows || []
        }
      }).catch(() => {})
    },
    startParkedPolling() {
      this.stopParkedPolling()
      this.parkedTimer = setInterval(() => {
        if (!document.hidden) this.loadParked()
      }, POLL_INTERVAL)
    },
    stopParkedPolling() {
      if (this.parkedTimer) {
        clearInterval(this.parkedTimer)
        this.parkedTimer = null
      }
    },
    onVisibilityChange() {
      if (!document.hidden) this.loadParked()
    },
    resumeParked(p) {
      if (this.acting) return
      const doResume = () => {
        this.acting = true
        orderResume(p.id).then(res => {
          if (res.code === 200) {
            this.orderId = p.id
            return this.loadOrder().then(() => {
              this.$message.success('已取单，可继续加菜或结算')
              this.loadParked()
            })
          }
          this.$message.error(res.message || '取单失败')
          return null
        }).catch(() => {
          this.$message.error('取单失败，请重试')
        }).finally(() => {
          this.acting = false
        })
      }
      if (this.cart.length) {
        this.$confirm({
          title: '取单确认',
          content: '当前待下单商品将被丢弃，确定取该挂单？',
          onOk: () => {
            this.cart = []
            doResume()
          }
        })
      } else {
        doResume()
      }
    },
    // ============ 建单/下单（挂单/结算共用前置） ============
    ensureOrder() {
      if (this.orderId) return Promise.resolve(this.orderId)
      return orderCreate({
        storeId: this.storeId,
        orderType: 'takeout',
        businessType: 'fastfood',
        isOut: this.isOut
      }).then(res => {
        if (res.code === 200 && res.data) {
          this.orderId = res.data.id
          return this.orderId
        }
        return Promise.reject(res)
      })
    },
    submitCart() {
      if (!this.cart.length) return Promise.resolve()
      return orderAddItems(this.orderId, {
        items: this.cart.map(line => ({
          materialId: line.materialId,
          quantity: line.quantity,
          optionValueIds: line.optionValueIds,
          remark: line.remark || undefined
        }))
      }).then(res => {
        if (res.code === 200) {
          this.cart = []
        } else {
          return Promise.reject(res)
        }
      })
    },
    loadOrder() {
      return orderDetail(this.orderId).then(res => {
        if (res.code === 200 && res.data) {
          this.order = res.data
          this.isOut = this.order.is_out || '1'
          return this.order
        }
        return Promise.reject(res)
      })
    },
    // 建单+下单+重读订单；仅"本次新建且尚未成功下单"的空单失败时回收（防残留空账单）；
    // 下单成功后读单失败不回收（防丢单），保留订单可重试结算；本地购物车保留
    submitBill() {
      const createdNow = !this.orderId
      let submitted = false
      return this.ensureOrder()
        .then(() => this.submitCart())
        .then(() => {
          submitted = true
          return this.loadOrder()
        })
        .catch(err => {
          if (createdNow && !submitted && this.orderId) {
            const failId = this.orderId
            this.orderId = null
            this.order = null
            orderCancel(failId).catch(() => {})
          }
          return Promise.reject(err)
        })
    },
    handlePark() {
      if (!this.canPark || this.acting) return
      this.acting = true
      this.parking = true
      this.submitBill().then(() => orderPark(this.orderId)).then(res => {
        if (res.code === 200) {
          this.$message.success('已挂单')
          this.resetBill()
          this.loadParked()
        } else {
          this.$message.error(res.message || '挂单失败')
          this.loadOrder()
        }
      }).catch(err => {
        this.$message.error((err && err.message) || '挂单失败，请重试')
        this.loadMenu()
      }).finally(() => {
        this.acting = false
        this.parking = false
      })
    },
    handleSettle() {
      if (!this.canSettle || this.acting) return
      this.acting = true
      this.parking = false
      this.submitBill().then(() => {
        this.member = null
        this.memberKeyword = ''
        this.memberResults = []
        this.eraseAmount = 0
        this.payType = 'cash'
        this.authCode = ''
        this.settleVisible = true
      }).catch(err => {
        this.$message.error((err && err.message) || '开单失败，请重试')
        this.loadMenu()
      }).finally(() => {
        this.acting = false
      })
    },
    resetBill() {
      this.orderId = null
      this.order = null
      this.cart = []
      this.isOut = '1'
    },
    // ==================== 退菜/作废（Task 8） ====================
    isReturned(it) {
      return String(it.return_status) === '1'
    },
    openReturnModal(it) {
      if (!this.orderId) return
      this.returnTarget = it
      this.returnVisible = true
    },
    confirmReturn(reason) {
      const it = this.returnTarget
      if (this.returnLoading || !this.orderId || !it) return
      this.returnLoading = true
      orderItemReturn(this.orderId, it.id, { reason }).then(res => {
        if (res.code === 200) {
          this.$message.success('退菜成功，已打印退菜单通知厨房')
          this.returnVisible = false
          this.loadOrder()
        } else {
          this.$message.error(res.message || '退菜失败')
        }
      }).catch(() => {
        this.$message.error('退菜失败，请重试')
      }).finally(() => {
        this.returnLoading = false
      })
    },
    // 整单作废：未结算整单取消（后端触发 ZFD 作废通知单打印），有未下单购物车一并丢弃
    handleVoid() {
      if (!this.orderId || this.acting) return
      this.$confirm({
        title: '整单作废',
        content: '作废后整单取消且不可恢复，将打印作废单通知厨房。确定作废？',
        okType: 'danger',
        okText: '确认作废',
        onOk: () => {
          this.acting = true
          orderCancel(this.orderId).then(res => {
            if (res.code === 200) {
              this.$message.success('整单已作废')
              this.resetBill()
              this.loadParked()
            } else {
              this.$message.error(res.message || '作废失败')
            }
          }).catch(() => {
            this.$message.error('作废失败，请重试')
          }).finally(() => {
            this.acting = false
          })
        }
      })
    },
    // ============ 结算弹窗（一期 Settle 重组） ============
    switchPayType(type) {
      this.payType = type
      this.authCode = ''
      clearTimeout(this.authTimer)
    },
    searchMember() {
      const kw = (this.memberKeyword || '').trim()
      if (!kw) {
        this.$message.warning('请输入手机号 / 姓名 / 卡号')
        return
      }
      this.memberSearching = true
      cashierMemberSearch(kw).then(res => {
        if (res.code === 200) {
          this.memberResults = (res.data || []).slice(0, 20)
          if (!this.memberResults.length) {
            this.$message.info('未找到会员')
          }
        } else {
          this.$message.error(res.message || '会员查询失败')
        }
      }).catch(() => {
        this.$message.error('会员查询失败，请重试')
      }).finally(() => {
        this.memberSearching = false
      })
    },
    pickMember(m) {
      this.member = {
        memberId: m.memberId,
        contacts: m.contacts || m.cardNo,
        telephone: m.telephone || '',
        levelName: m.levelName || ''
      }
      this.memberResults = []
      this.memberKeyword = ''
    },
    clearMember() {
      this.member = null
    },
    confirmSettle() {
      if (this.settling || !this.orderId) return
      const body = { payType: this.payType, eraseAmount: Number(this.eraseAmount) || 0 }
      if (this.member && this.member.memberId) body.memberId = this.member.memberId
      if (this.payType !== 'cash') {
        const code = (this.authCode || '').trim()
        if (!code) {
          this.$message.warning('请扫描顾客付款码')
          return
        }
        if (!/^1\d{15,18}$/.test(code) && !/^(2[5-9]|30)\d{10,28}$/.test(code)) {
          this.$message.warning('付款码格式不正确，请确认为微信或支付宝付款码')
          return
        }
        clearTimeout(this.authTimer)
        this.authCode = ''
        body.authCode = code
      }
      this.settling = true
      orderSettle(this.orderId, body).then(res => {
        if (res.code === 200 && res.data) {
          const result = res.data
          this.settleVisible = false
          this.resetBill()
          this.settleResult = result
          this.callVisible = true
          // 取餐语音播报（7.2）：事件总线 → CashierLayout 统一 TTS/降级链
          if (result.callNo) {
            this.$root.$emit('dining-call-speech', { orderId: result.orderId, callNo: result.callNo })
          }
          this.loadParked()
          // 结账单打印（Task 5）：autoPrint 开启→静默出票，关闭→弹预览；fire-and-forget 不阻塞收银流程
          if (result && result.orderId) {
            PrintModal.isAutoPrintEnabled().then(enabled => {
              if (enabled) {
                this.$refs.printModal.printSilent(result.orderId, 'settle')
              } else {
                this.$refs.printModal.show(result.orderId, 'settle')
              }
            })
          }
        } else {
          this.$message.error(res.message || '结算失败')
        }
      }).catch(() => {
        this.$message.error('结算失败，请重试')
      }).finally(() => {
        this.settling = false
      })
    }
  }
}
</script>

<style scoped>
.fastfood-view {
  flex: 1;
  display: flex;
  min-height: 0;
  background: #f5f6f8;
}
/* 左侧：分类 + 商品网格 */
.ff-main {
  flex: 1;
  display: flex;
  min-width: 0;
}
.ff-cats {
  width: 130px;
  flex-shrink: 0;
  background: #fff;
  border-right: 1px solid #e8ebf0;
  overflow-y: auto;
}
.ff-cat {
  padding: 12px 14px;
  font-size: 14px;
  color: #4a5261;
  cursor: pointer;
  border-left: 3px solid transparent;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ff-cat:hover {
  color: #1890ff;
}
.ff-cat.active {
  color: #1890ff;
  background: #e6f7ff;
  border-left-color: #1890ff;
  font-weight: 600;
}
.ff-goods {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 12px 14px;
}
.ff-goods-head {
  flex-shrink: 0;
  margin-bottom: 12px;
}
.ff-loading {
  font-size: 22px;
  color: #1890ff;
  margin: 40px auto;
  display: block;
  text-align: center;
}
.ff-grid {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  align-content: start;
  padding-bottom: 12px;
}
.ff-card {
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid #eef0f3;
  transition: box-shadow 0.2s;
  user-select: none;
}
.ff-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}
.ff-card.soldout {
  opacity: 0.5;
  cursor: not-allowed;
}
.ff-card-img {
  height: 92px;
  background: #f5f6f8;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ff-card-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.ff-img-holder {
  font-size: 26px;
  color: #cfd4db;
  font-weight: 600;
}
.ff-soldout {
  position: absolute;
  top: 6px;
  right: 6px;
  background: #ff4d4f;
  color: #fff;
  font-size: 12px;
  padding: 1px 8px;
  border-radius: 10px;
}
.ff-card-name {
  font-size: 14px;
  font-weight: 600;
  color: #262b33;
  padding: 8px 10px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ff-card-price {
  color: #e4632a;
  font-weight: 700;
  font-size: 16px;
  padding: 4px 10px 10px;
}
.ff-goods-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #98a0aa;
  font-size: 14px;
}
/* 右侧：挂单 + 购物车 */
.ff-side {
  width: 340px;
  flex-shrink: 0;
  background: #fff;
  border-left: 1px solid #e8ebf0;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.ff-parked {
  flex-shrink: 0;
  border-bottom: 1px solid #eef0f3;
}
.ff-parked-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 600;
  color: #4a5261;
  cursor: pointer;
  user-select: none;
}
.ff-parked-head:hover {
  color: #1890ff;
}
.ff-parked-list {
  max-height: 180px;
  overflow-y: auto;
  padding: 0 12px 10px;
}
.ff-parked-card {
  border: 1px solid #eef0f3;
  border-radius: 8px;
  padding: 8px 10px;
  margin-bottom: 8px;
  cursor: pointer;
}
.ff-parked-card:hover {
  border-color: #722ed1;
  box-shadow: 0 2px 8px rgba(114, 46, 209, 0.15);
}
.fp-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.fp-no {
  font-size: 12px;
  color: #722ed1;
  font-weight: 600;
}
.fp-amount {
  font-size: 14px;
  font-weight: 700;
  color: #e4632a;
}
.fp-time {
  font-size: 11px;
  color: #98a0aa;
  margin-top: 3px;
}
.fp-empty {
  color: #cfd4db;
  text-align: center;
  font-size: 12px;
  padding: 8px 0;
}
.ff-cart {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.ff-cart-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px 6px;
  font-size: 14px;
  font-weight: 600;
  color: #262b33;
  flex-shrink: 0;
}
.ff-clear {
  font-size: 12px;
  color: #98a0aa;
}
.ff-clear:hover {
  color: #ff4d4f;
}
.ff-cart-scroll {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 0 12px;
}
.ff-group-label {
  font-size: 12px;
  color: #98a0aa;
  padding: 6px 4px;
}
.ff-line {
  border: 1px solid #f0f0f3;
  border-radius: 8px;
  padding: 8px 10px;
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
}
.ff-line.saved {
  background: #fafbfc;
}
.fl-main {
  flex: 1;
  min-width: 0;
}
.fl-name {
  font-size: 13px;
  color: #262b33;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fl-sub {
  font-size: 12px;
  color: #98a0aa;
  margin-top: 2px;
  word-break: break-all;
}
.fl-right {
  text-align: right;
  flex-shrink: 0;
}
.fl-qty {
  font-size: 12px;
  color: #98a0aa;
}
.fl-amount {
  font-size: 14px;
  color: #e4632a;
  font-weight: 600;
  margin-top: 4px;
}
/* 已退行（Task 8）：置灰+划线+已退标签；未退行行内退菜按钮 */
.ff-line-returned .fl-name,
.ff-line-returned .fl-qty,
.ff-line-returned .fl-amount {
  color: #c0c6cf;
  text-decoration: line-through;
}
.fl-returned-tag {
  margin-left: 6px;
}
.fl-return-btn {
  margin-top: 6px;
  padding: 0 8px;
}
.qty-step {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}
.qty-num {
  min-width: 28px;
  text-align: center;
  font-weight: 600;
}
.fl-del {
  margin-left: auto;
}
.ff-cart-empty {
  color: #cfd4db;
  text-align: center;
  padding: 40px 0;
  font-size: 13px;
}
/* 底部结算区 */
.ff-footer {
  flex-shrink: 0;
  border-top: 1px solid #eef0f3;
  padding: 10px 14px 12px;
}
.ff-out-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.ff-out-tip {
  font-size: 11px;
  color: #98a0aa;
}
.ff-fee-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #4a5261;
  padding: 3px 0;
}
.ff-total {
  align-items: baseline;
  font-size: 14px;
  font-weight: 600;
  padding-top: 6px;
}
.ff-total-num {
  font-size: 22px;
  font-weight: 700;
  color: #e4632a;
}
.ff-actions {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}
.ff-actions .ant-btn {
  flex: 1;
}
.ff-settle-btn {
  font-weight: 600;
}
/* 结算弹窗 */
.st-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.st-amounts {
  background: #f6f8fa;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 6px;
}
.st-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #4a5261;
  padding: 3px 0;
}
.st-payable {
  font-size: 18px;
  font-weight: 700;
  color: #e4632a;
}
.st-sec-title {
  font-size: 13px;
  font-weight: 600;
  color: #262b33;
  margin-top: 6px;
}
.st-member {
  display: flex;
  align-items: center;
  gap: 10px;
}
.st-member-name {
  font-weight: 600;
  color: #262b33;
}
.st-member-phone {
  flex: 1;
  color: #98a0aa;
  font-size: 12px;
}
.st-member-search {
  display: flex;
  gap: 8px;
}
.st-member-results {
  margin-top: 8px;
  max-height: 150px;
  overflow-y: auto;
  border: 1px solid #f0f0f3;
  border-radius: 6px;
}
.st-member-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  cursor: pointer;
  font-size: 13px;
}
.st-member-item:hover {
  background: #f5f8ff;
}
.smi-name {
  font-weight: 600;
  color: #262b33;
}
.smi-phone {
  color: #98a0aa;
  flex: 1;
}
.st-tip {
  font-size: 12px;
  color: #c0c6cf;
}
.st-pay-types {
  display: flex;
  gap: 8px;
}
.st-pay-type {
  flex: 1;
  text-align: center;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  padding: 9px 0;
  cursor: pointer;
  font-size: 13px;
  color: #4a5261;
  user-select: none;
}
.st-pay-type.active {
  background: #1890ff;
  border-color: #1890ff;
  color: #fff;
  font-weight: 600;
}
.st-confirm {
  margin-top: 14px;
}
/* 取餐号大字弹窗 */
.callno-wrap {
  text-align: center;
  padding: 18px 6px 8px;
}
.callno-label {
  font-size: 15px;
  color: #4a5261;
}
.callno-num {
  font-size: 84px;
  font-weight: 800;
  color: #1890ff;
  letter-spacing: 4px;
  line-height: 1.2;
  margin: 10px 0;
}
.callno-sub {
  font-size: 13px;
  color: #98a0aa;
  margin-bottom: 18px;
}
</style>
